import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Adventure, InstanceSummary, Section } from '@gm-binder/shared';
import { api, mediaUrl } from '../api/client';
import { AppShell } from '../components/layout/AppShell';
import {
  PageHeader,
  Card,
  Badge,
  Button,
  IconButton,
  Switch,
  Modal,
  Input,
  Field,
  MediaImage,
  MarkdownView,
  LoadingState,
  EmptyState,
} from '../components/ui';
import { useUiStore } from '../store/uiStore';
import styles from './AdventureDetailPage.module.css';

export function AdventureDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const toast = useUiStore((s) => s.toast);
  const [adventure, setAdventure] = useState<Adventure | null>(null);
  const [runs, setRuns] = useState<InstanceSummary[]>([]);
  const [notFound, setNotFound] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [runName, setRunName] = useState('');

  const reload = async () => {
    try {
      const adv = await api.getAdventure(slug);
      setAdventure(adv);
      const all = await api.listInstances();
      setRuns(all.filter((i) => i.adventureSlug === slug));
    } catch {
      setNotFound(true);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const sortedSections = useMemo(
    () => (adventure ? [...adventure.sections].sort((a, b) => a.order - b.order) : []),
    [adventure],
  );

  const persistSections = async (sections: Section[]) => {
    if (!adventure) return;
    const reordered = sections.map((s, i) => ({ ...s, order: i }));
    setAdventure({ ...adventure, sections: reordered });
    try {
      await api.updateAdventure(adventure.slug, { sections: reordered });
    } catch {
      toast('Salvataggio sezioni fallito', 'danger');
    }
  };

  const toggleSection = (id: string) =>
    persistSections(
      sortedSections.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)),
    );

  const moveSection = (index: number, dir: -1 | 1) => {
    const next = [...sortedSections];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    void persistSections(next);
  };

  const startRun = async () => {
    if (!adventure) return;
    try {
      const inst = await api.createInstance({
        adventureSlug: adventure.slug,
        name: runName.trim() || undefined,
      });
      navigate(`/run/${inst.id}`);
    } catch {
      toast('Avvio sessione fallito', 'danger');
    }
  };

  const deleteAdventure = async () => {
    if (!adventure) return;
    if (!confirm(`Eliminare "${adventure.title}"? L'azione è irreversibile.`)) return;
    try {
      await api.deleteAdventure(adventure.slug);
      toast('Avventura eliminata', 'info');
      navigate('/library');
    } catch {
      toast('Eliminazione fallita', 'danger');
    }
  };

  const deleteRun = async (id: string) => {
    if (!confirm('Eliminare questa sessione salvata?')) return;
    await api.deleteInstance(id);
    setRuns((rs) => rs.filter((r) => r.id !== id));
  };

  if (notFound)
    return (
      <AppShell>
        <EmptyState
          icon="⚠️"
          title="Avventura non trovata"
          actions={
            <Link to="/library">
              <Button variant="primary">Torna alla libreria</Button>
            </Link>
          }
        />
      </AppShell>
    );

  if (!adventure)
    return (
      <AppShell>
        <LoadingState />
      </AppShell>
    );

  return (
    <AppShell>
      <Link to="/library" className={styles.back}>
        ← Libreria
      </Link>

      <div className={styles.hero}>
        <MediaImage
          src={mediaUrl(adventure.slug, adventure.cover)}
          alt={adventure.title}
          fallback="🕯️"
          className={styles.cover}
        />
        <div className={styles.heroBody}>
          <PageHeader
            eyebrow="Avventura"
            title={adventure.title}
            subtitle={adventure.subtitle}
            actions={
              <>
                <Button variant="primary" icon="▶" onClick={() => setStartOpen(true)}>
                  Avvia sessione
                </Button>
                <Link to={`/library/${adventure.slug}/edit`}>
                  <Button icon="✎">Modifica</Button>
                </Link>
                <IconButton label="Elimina avventura" onClick={deleteAdventure}>
                  🗑️
                </IconButton>
              </>
            }
          />
          <div className={styles.metaRow}>
            {adventure.meta.level != null && <Badge tone="gold">Livello {adventure.meta.level}</Badge>}
            {adventure.meta.playerCount && <Badge>👥 {adventure.meta.playerCount}</Badge>}
            {adventure.meta.duration && <Badge>⏳ {adventure.meta.duration}</Badge>}
          </div>
          {adventure.meta.tagline && <p className={styles.tagline}>{adventure.meta.tagline}</p>}
        </div>
      </div>

      <div className={styles.columns}>
        <div className={styles.col}>
          {adventure.overview.prologue && (
            <Card>
              <h3>Prologo</h3>
              <div className={styles.clamp}>
                <MarkdownView>{adventure.overview.prologue}</MarkdownView>
              </div>
            </Card>
          )}

          <Card>
            <h3>Sessioni salvate</h3>
            {runs.length === 0 ? (
              <p className={styles.muted}>
                Nessuna sessione ancora. Premi “Avvia sessione” per cominciare a giocare.
              </p>
            ) : (
              <div className={styles.runList}>
                {runs.map((r) => (
                  <div key={r.id} className={styles.runRow}>
                    <Link to={`/run/${r.id}`} className={styles.runName}>
                      {r.name}
                    </Link>
                    <Badge
                      tone={
                        r.status === 'active' ? 'success' : r.status === 'paused' ? 'warn' : 'neutral'
                      }
                    >
                      {r.status === 'active' ? 'In corso' : r.status === 'paused' ? 'In pausa' : 'Conclusa'}
                    </Badge>
                    <span className={styles.runDate}>
                      {new Date(r.updatedAt).toLocaleDateString('it-IT')}
                    </span>
                    <Link to={`/run/${r.id}`}>
                      <Button size="sm">Riprendi</Button>
                    </Link>
                    <IconButton label="Elimina sessione" size="sm" onClick={() => deleteRun(r.id)}>
                      ✕
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className={styles.col}>
          <Card>
            <h3>Sezioni del binder</h3>
            <p className={styles.muted}>
              Attiva, disattiva e riordina le sezioni che vedrai durante la sessione.
            </p>
            <div className={styles.sectionList}>
              {sortedSections.map((s, i) => (
                <div key={s.id} className={styles.sectionRow}>
                  <span className={styles.sectionIcon} aria-hidden>
                    {s.icon}
                  </span>
                  <span className={styles.sectionTitle}>{s.title}</span>
                  <div className={styles.reorder}>
                    <IconButton
                      label="Su"
                      size="sm"
                      disabled={i === 0}
                      onClick={() => moveSection(i, -1)}
                    >
                      ↑
                    </IconButton>
                    <IconButton
                      label="Giù"
                      size="sm"
                      disabled={i === sortedSections.length - 1}
                      onClick={() => moveSection(i, 1)}
                    >
                      ↓
                    </IconButton>
                  </div>
                  <Switch checked={s.enabled} onChange={() => toggleSection(s.id)} />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <Modal open={startOpen} onClose={() => setStartOpen(false)} title="Avvia una sessione">
        <Field label="Nome della sessione" hint="Es. il nome del gruppo o la data. Potrai riprenderla più tardi.">
          <Input
            value={runName}
            autoFocus
            placeholder={`${adventure.title} — Sessione ${runs.length + 1}`}
            onChange={(e) => setRunName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void startRun();
            }}
          />
        </Field>
        <div className={styles.modalActions}>
          <Button onClick={() => setStartOpen(false)}>Annulla</Button>
          <Button variant="primary" icon="▶" onClick={startRun}>
            Inizia
          </Button>
        </div>
      </Modal>
    </AppShell>
  );
}
